import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import {
  getProduct, formatPrice, categoryLabel,
  createMarketplaceBill, claimFreeEbook, getMarketplaceDownloadUrl,
} from '../services/marketplaceService';
import { toast } from './toastBus';

// ═══════════════════════════════════════════════════════════
//  MARKETPLACE — single product detail + buy
// ═══════════════════════════════════════════════════════════

const CAT_ICONS = { ebook: 'menu_book', tiktok_account: 'music_note', flow_ultra: 'bolt' };

export default function MarketplaceProductView() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showBuy, setShowBuy] = useState(false);
  const [whatsapp, setWhatsapp] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setProduct(await getProduct(productId));
      } catch (e) {
        toast.error('Gagal muat produk: ' + (e?.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [productId]);

  if (loading) return <div style={S.wrap}><div className="sesaat-m-tight" style={S.page}><div style={S.muted}>Memuatkan…</div></div></div>;
  if (!product) return <div style={S.wrap}><div className="sesaat-m-tight" style={S.page}><div style={S.muted}>Produk tidak dijumpai.</div></div></div>;

  const isFree = product.pricingMode === 'free';
  const soldOut = product.status === 'sold_out' || product.stock === 0;
  const isManual = product.fulfillment === 'manual';
  const isAutoStock = product.fulfillment === 'auto_stock';
  const tempoh = Number(product.durationDays) || 0;
  const disc = product.originalPrice > product.price && !isFree
    ? Math.round((1 - product.price / product.originalPrice) * 100) : 0;

  async function handleFree() {
    if (!auth.currentUser) { toast.error('Sila log masuk dahulu.'); return; }
    setBusy(true);
    try {
      const { orderId } = await claimFreeEbook(product.id);
      try {
        const { url } = await getMarketplaceDownloadUrl(orderId);
        window.open(url, '_blank');
      } catch { /* download can be retried from locker */ }
      toast.success('Berjaya! Ebook dibuka. Boleh muat turun semula di Pembelian Saya.');
      navigate('/marketplace/pembelian');
    } catch (e) {
      toast.error('Gagal: ' + (e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  // Bayaran layan diri. Server cipta pesanan + bil (harga dibaca di sana),
  // kemudian pelanggan dipindahkan ke Billplz. Webhook yang melepaskan akses.
  // ⚠️ `setBusy(false)` sengaja TIDAK dipanggil pada laluan berjaya — halaman
  //    sedang berpindah, dan butang yang hidup semula mengundang klik kedua.
  async function handleBuy() {
    if (!auth.currentUser) { toast.error('Sila log masuk dahulu.'); return; }
    setBusy(true);
    try {
      const { url } = await createMarketplaceBill(product, { whatsapp: whatsapp.trim() });
      window.location.href = url;
      return;
    } catch (e) {
      toast.error('Gagal mula pembayaran: ' + (e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={S.wrap}>
      <div className="sesaat-m-tight" style={S.page}>
        <button className="mktp-back" style={S.back} onClick={() => navigate('/marketplace')}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span> Kembali ke Marketplace
        </button>

        <div style={S.layout}>
          {/* ─── Image ─── */}
          <div style={{ ...S.hero, backgroundImage: product.imageUrl ? `url(${product.imageUrl})` : 'none' }}>
            {!product.imageUrl && (
              <span className="material-symbols-outlined" style={{ color: '#c7d2fe', fontSize: 56 }}>
                {CAT_ICONS[product.category] || 'storefront'}
              </span>
            )}
            {soldOut && (
              <div style={S.soldOutOverlay}><span style={S.soldOutText}>SOLD OUT</span></div>
            )}
          </div>

          {/* ─── Info ─── */}
          <div style={S.info}>
            <div style={S.catRow}>
              <span style={S.catPill}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{CAT_ICONS[product.category]}</span>
                {categoryLabel(product.category)}
              </span>
              {typeof product.stock === 'number' && !soldOut && (
                <span style={S.stockPill}>Stok: {product.stock}</span>
              )}
            </div>

            <h1 style={S.title}>{product.title}</h1>

            <div style={S.priceCard}>
              {isFree ? (
                <span style={S.free}>PERCUMA</span>
              ) : (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <span style={S.price}>{formatPrice(product.price)}</span>
                  {disc > 0 && (
                    <>
                      <span style={S.orig}>{formatPrice(product.originalPrice)}</span>
                      <span style={S.discBadge}>Jimat {disc}%</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Delivery explainer */}
            <div style={S.deliveryInfo}>
              <span className="material-symbols-outlined" style={{ fontSize: 20, color: '#4f46e5' }}>
                {isManual ? 'support_agent' : isAutoStock ? 'key' : 'download'}
              </span>
              <div>
                <div style={S.deliveryTitle}>
                  {isManual ? 'Penghantaran oleh admin' : isAutoStock ? 'Akses akaun automatik' : 'Muat turun automatik'}
                </div>
                <div style={S.deliveryDesc}>
                  {isManual
                    ? 'Selepas pembayaran disahkan, admin akan menyerahkan akses anda secara manual. Semak status di Pembelian Saya.'
                    : isAutoStock
                      ? `Sebaik pembayaran selesai, butiran log masuk dilepaskan serta-merta di Pembelian Saya${tempoh > 0 ? ` — akses sah selama ${tempoh} hari` : ''}. Tiada penantian, tiada perlu menghubungi admin.`
                      : isFree
                        ? 'Klik dan terus muat turun — tiada bayaran diperlukan.'
                        : 'Selepas pembayaran disahkan, anda boleh terus memuat turun fail di Pembelian Saya.'}
                </div>
              </div>
            </div>

            {product.description && <p style={S.desc}>{product.description}</p>}

            {/* CTA */}
            {soldOut ? (
              <button style={S.disabledBtn} disabled>
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>block</span> Sudah habis (Sold Out)
              </button>
            ) : isFree ? (
              <button className="mktp-cta" style={{ ...S.cta, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={handleFree}>
                <span className="material-symbols-outlined" style={{ fontSize: 19 }}>download</span>
                {busy ? 'Memproses…' : 'Dapatkan Percuma'}
              </button>
            ) : !showBuy ? (
              <button className="mktp-cta" style={S.cta} onClick={() => setShowBuy(true)}>
                <span className="material-symbols-outlined" style={{ fontSize: 19 }}>shopping_cart</span>
                Beli — {formatPrice(product.price)}
              </button>
            ) : (
              <div style={S.buyPanel}>
                <div style={S.buyPanelTitle}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#4f46e5' }}>receipt_long</span>
                  Teruskan Pembayaran
                </div>
                <p style={S.note}>
                  Anda akan dibawa ke halaman pembayaran Billplz (FPX atau kad).
                  {isAutoStock
                    ? ' Sebaik bayaran selesai, akses anda dilepaskan serta-merta di '
                    : ' Sebaik bayaran selesai, pesanan anda muncul di '}
                  <strong>Pembelian Saya</strong>.
                </p>
                <label style={S.label}>No. WhatsApp (opsyenal — untuk admin hubungi jika perlu)
                  <input style={S.input} value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="cth 60123456789" />
                </label>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button className="mktp-cta" style={{ ...S.cta, marginTop: 0, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={handleBuy}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>verified_user</span>
                    {busy ? 'Menyediakan…' : `Bayar ${formatPrice(product.price)}`}
                  </button>
                  <button style={S.ghost} disabled={busy} onClick={() => setShowBuy(false)}>Batal</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .mktp-back:hover { color: #4f46e5 !important; }
        .mktp-cta { transition: transform .15s ease, box-shadow .15s ease; }
        .mktp-cta:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(79,70,229,.35); }
        @media (max-width: 760px) { .mktp-layout { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}

const S = {
  wrap: { height: '100%', overflowY: 'auto', background: '#f8fafc' },
  page: { padding: '28px 32px 48px', maxWidth: 1020, margin: '0 auto' },
  back: { display: 'inline-flex', alignItems: 'center', gap: 5, border: 'none', background: 'transparent', color: '#6b7280', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 18, fontFamily: 'inherit', padding: 0 },
  layout: { display: 'grid', gridTemplateColumns: 'minmax(0, 420px) 1fr', gap: 32, alignItems: 'start' },
  hero: { aspectRatio: '1 / 1', background: '#eef2ff center/cover no-repeat', borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', border: '1px solid #e8eaf0', boxShadow: '0 1px 3px rgba(16,24,40,.05)' },
  soldOutOverlay: { position: 'absolute', inset: 0, background: 'rgba(255,255,255,.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'grayscale(1)' },
  soldOutText: { background: '#111827', color: '#fff', fontSize: 14, fontWeight: 800, padding: '8px 22px', borderRadius: 999, letterSpacing: '.08em', transform: 'rotate(-6deg)' },
  info: { minWidth: 0 },
  catRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  catPill: { display: 'inline-flex', alignItems: 'center', gap: 5, background: '#eef2ff', color: '#4f46e5', fontSize: 11.5, fontWeight: 800, padding: '4px 12px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '.03em' },
  stockPill: { display: 'inline-flex', alignItems: 'center', background: '#f3f4f6', color: '#374151', fontSize: 11.5, fontWeight: 700, padding: '4px 12px', borderRadius: 999 },
  title: { fontSize: 26, fontWeight: 800, color: '#111827', margin: '12px 0 14px', lineHeight: 1.25, letterSpacing: '-0.02em' },
  priceCard: { padding: '14px 18px', background: '#fff', border: '1px solid #e8eaf0', borderRadius: 14, marginBottom: 14, boxShadow: '0 1px 3px rgba(16,24,40,.05)' },
  price: { fontWeight: 800, fontSize: 28, color: '#4f46e5' },
  orig: { fontSize: 16, color: '#9ca3af', textDecoration: 'line-through' },
  discBadge: { background: '#fef2f2', color: '#e11d48', fontSize: 12, fontWeight: 800, padding: '3px 10px', borderRadius: 999 },
  free: { fontWeight: 800, fontSize: 24, color: '#059669' },
  deliveryInfo: { display: 'flex', gap: 12, padding: '13px 16px', background: '#fff', border: '1px solid #e8eaf0', borderRadius: 14, marginBottom: 16, alignItems: 'flex-start', boxShadow: '0 1px 3px rgba(16,24,40,.05)' },
  deliveryTitle: { fontSize: 13.5, fontWeight: 700, color: '#111827' },
  deliveryDesc: { fontSize: 12.5, color: '#6b7280', lineHeight: 1.55, marginTop: 2 },
  desc: { color: '#374151', fontSize: 14, lineHeight: 1.75, whiteSpace: 'pre-wrap', margin: '0 0 8px' },
  cta: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 26px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #4f46e5, #6d28d9)', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 14, fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(79,70,229,.3)' },
  disabledBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '13px 26px', borderRadius: 12, border: 'none', background: '#e5e7eb', color: '#9ca3af', fontWeight: 700, fontSize: 15, cursor: 'not-allowed', marginTop: 14, fontFamily: 'inherit' },
  ghost: { padding: '13px 20px', borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' },
  buyPanel: { marginTop: 16, padding: 18, border: '1px solid #e8eaf0', borderRadius: 16, background: '#fff', boxShadow: '0 1px 3px rgba(16,24,40,.05)' },
  buyPanelTitle: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14.5, fontWeight: 800, color: '#111827', marginBottom: 8 },
  note: { fontSize: 13, color: '#6b7280', lineHeight: 1.6, marginTop: 0 },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 },
  input: { padding: '9px 12px', borderRadius: 9, border: '1px solid #d1d5db', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' },
  muted: { color: '#9ca3af', fontSize: 14, padding: '24px 0' },
};
